import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { templatesActions } from "../../redux/templates/templatesSlice";
import type { TemplateCategory } from "../../redux/templates/templatesTypes";

const createScopeKey = (
  platformId?: string,
  clientId?: string,
  templateCategory?: TemplateCategory,
): string => `${templateCategory ?? ""}|${platformId ?? ""}|${clientId ?? ""}`;

const determineTemplateCategory = (
  platformId?: string,
  clientId?: string,
): TemplateCategory => {
  if (platformId && clientId) return "CLIENT";
  if (platformId) return "PLATFORM";
  return "OPTUM";
};

interface UseGetTemplatesOptions {
  platformId?: string;
  clientId?: string;
  token?: string;
  enabled?: boolean;
}

export const useGetTemplates = ({
  platformId,
  clientId,
  token,
  enabled = true,
}: UseGetTemplatesOptions) => {
  const dispatch = useAppDispatch();
  const lastDispatchedKeyRef = useRef<string | null>(null);
  const optumFetchedRef = useRef(false);
  const platformFetchedForPlatformIdRef = useRef<string | null>(null);
  const dataByCategory = useAppSelector(
    (state) => state.templates.dataByCategory,
  );
  const loading = useAppSelector((state) => state.templates.loading);

  useEffect(() => {
    if (!enabled || !token) {
      return;
    }

    if (!optumFetchedRef.current) {
      dispatch(
        templatesActions.getTemplatesRequested({
          platformId: "",
          clientId: "",
          templateCategory: "OPTUM",
          token,
        }),
      );
      optumFetchedRef.current = true;
    }
  }, [enabled, token, dispatch]);

  useEffect(() => {
    if (!enabled || !token || loading) {
      return;
    }

    const hasOptumData = !!dataByCategory.OPTUM;

    if (
      platformId &&
      platformFetchedForPlatformIdRef.current !== platformId &&
      hasOptumData
    ) {
      dispatch(
        templatesActions.getTemplatesRequested({
          platformId,
          clientId: "",
          templateCategory: "PLATFORM",
          token,
        }),
      );
      platformFetchedForPlatformIdRef.current = platformId;
    }
  }, [platformId, enabled, token, dataByCategory.OPTUM, loading, dispatch]);

  useEffect(() => {
    if (!enabled || !token || loading) {
      return;
    }

    const hasOptumData = !!dataByCategory.OPTUM;
    const hasPlatformData = !!dataByCategory.PLATFORM;

    if (!platformId || !clientId) {
      return;
    }

    const category = determineTemplateCategory(platformId, clientId);
    if (category !== "CLIENT") {
      return;
    }

    if (!hasOptumData || (platformId && !hasPlatformData)) {
      return;
    }

    const scopeKey = createScopeKey(platformId, clientId, category);
    if (scopeKey === lastDispatchedKeyRef.current) {
      return;
    }

    lastDispatchedKeyRef.current = scopeKey;
    dispatch(
      templatesActions.getTemplatesRequested({
        platformId,
        clientId,
        templateCategory: category,
        token,
      }),
    );
  }, [
    platformId,
    clientId,
    token,
    enabled,
    dataByCategory.OPTUM,
    dataByCategory.PLATFORM,
    loading,
    dispatch,
  ]);
};
